/**
 * Files the user picked, held in this page and nowhere else.
 *
 * 🔴 THE BYTES NEVER LEAVE THE PAGE. The model is handed a handle and some
 *    metadata; the real File sits in this map until dispatch swaps it back and
 *    hands it to the customer's own endpoint. Verb's servers never receive it,
 *    which is what keeps docs/18's four objections to file support, storage,
 *    retention, scanning and PII, from applying at all rather than being
 *    argued with. docs/40 section 3.
 *
 *    It is the same sentence execute.ts already carries about credentials, with
 *    one more noun: the credential and the bytes both stay here.
 */

export interface FileHandleMeta {
  readonly handle: string;
  /** As the user's disk had it. Untrusted text: it reaches the model as metadata. */
  readonly name: string;
  /** The browser's mime, which is a better signal than the extension. */
  readonly type: string;
  readonly size: number;
}

const registry = new Map<string, File>();
const previews = new Map<string, string>();

let counter = 0;

/**
 * 🔴 PREFIXED AND CHECKED, NEVER PARSED. The widget decides whether a string is
 *    a handle by asking this registry, never by pattern-matching a value that
 *    came back through the model. See `resolve`.
 */
function mint(): string {
  counter += 1;
  const rand = Math.random().toString(36).slice(2, 8);
  return `file_${counter.toString(36)}${rand}`;
}

export function register(file: File): FileHandleMeta {
  const handle = mint();
  registry.set(handle, file);
  return { handle, name: file.name, type: file.type, size: file.size };
}

export function isHandle(value: unknown): value is string {
  return typeof value === "string" && registry.has(value);
}

/**
 * The File behind a handle.
 *
 * 🔴 A HANDLE THE REGISTRY DOES NOT KNOW IS AN ERROR, NEVER A PASS-THROUGH.
 *    The model can emit any string it likes. If it invents `file_deadbeef` and
 *    the widget quietly forwards that literal, the customer's API receives the
 *    word `file_deadbeef` where a photograph should be, stores it, and nothing
 *    reports a problem. Callers must treat null as a failed tool call.
 */
export function resolve(handle: string): File | null {
  return registry.get(handle) ?? null;
}

/**
 * A local object URL for an image thumbnail.
 *
 * Never an upload, and never sent anywhere: this is the browser showing the
 * user their own file back.
 */
export function previewUrl(handle: string): string | null {
  const existing = previews.get(handle);
  if (existing) return existing;

  const file = registry.get(handle);
  if (!file || !file.type.startsWith("image/")) return null;

  const url = URL.createObjectURL(file);
  previews.set(handle, url);
  return url;
}

/**
 * Drops everything at the end of a turn.
 *
 * 🔴 HANDLES ARE PER TURN. A panel left open for an afternoon must not be
 *    holding twenty megabytes of somebody's PDFs, and a handle from an earlier
 *    turn must fail like any unknown one rather than reaching for a stale File.
 *    The object URLs are revoked here too, or the browser keeps the blob alive
 *    even after the map forgets it.
 */
export function clear(): void {
  for (const url of previews.values()) URL.revokeObjectURL(url);
  previews.clear();
  registry.clear();
}

/** Test seam. Never called from widget code. */
export function __size(): number {
  return registry.size;
}
